import { ChevronDown, ChevronRight, LogOut, Menu, Moon, PanelLeft, Sun, UserRound } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Button } from "../../shared/components/Button";
import { Brand } from "../../shared/components/Brand";
import { Dialog } from "../../shared/components/Dialog";
import { NavigationSearch } from "../../shared/components/NavigationSearch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../../shared/components/ui/dropdown-menu";
import { cn } from "../../shared/components/cn";
import { defaultAdminNavigation, type AdminNavigationGroup } from "../navigation";
export type { AdminNavigationGroup, AdminNavigationItem } from "../navigation";

export interface AdminOperator {
  displayName?: string | undefined;
  email: string;
  roleLabel?: string | undefined;
}
export interface AdminLayoutProps {
  operator?: AdminOperator | undefined;
  navigation?: AdminNavigationGroup[] | undefined;
  onLogout?: (() => void) | undefined;
  isLoggingOut?: boolean | undefined;
  children?: ReactNode | undefined;
}
export function AdminLayout({ operator, navigation = defaultAdminNavigation, onLogout, isLoggingOut = false, children }: AdminLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  const menuRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();
  const current = navigation.flatMap((group) => group.items).find((item) =>
    item.to === "/" ? pathname === "/" : pathname === item.to || pathname.startsWith(item.to + "/")
  );
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);
  useEffect(() => {
    const media = window.matchMedia("(min-width: 1024px)");
    const close = () => { if (media.matches) setMobileOpen(false); };
    media.addEventListener("change", close);
    return () => media.removeEventListener("change", close);
  }, []);
  function toggleTheme() {
    setDark((value) => !value);
  }
  const sidebar = (
    <div className="flex h-full flex-col">
      <Link to="/" onClick={() => setMobileOpen(false)} className="flex h-24 shrink-0 items-center border-b px-5" aria-label="Gunbound Admin — visão geral"><Brand /></Link>
      <div className="px-3 pb-2 pt-5"><NavigationSearch navigation={navigation} shortcut={!mobileOpen} /></div>
      <nav aria-label="Navegação principal" className="min-h-0 flex-1 space-y-6 overflow-y-auto px-3 py-3">
        {navigation.map((group, index) => (
          <div key={group.label ?? index}>
            {group.label && <p className="mb-2 px-3 text-xs font-medium text-muted-foreground">{group.label}</p>}
            <ul className="space-y-1">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink to={item.to} end={item.end ?? false} onClick={() => setMobileOpen(false)}
                    className={({ isActive }) => cn("flex min-h-9 items-center gap-3 rounded-md px-3 py-2 text-[13px] transition-colors hover:bg-muted",
                      isActive ? "bg-muted font-medium text-foreground" : "text-zinc-600 dark:text-zinc-400")}>
                    <item.icon className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.6} aria-hidden="true" />
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="flex h-14 shrink-0 items-center justify-between border-t px-4">
        <span className="text-xs text-muted-foreground">Painel administrativo</span>
        <Button variant="ghost" size="icon" className="hidden size-8 lg:inline-flex" aria-label="Recolher navegação" onClick={() => setCollapsed(true)}><PanelLeft className="size-4" /></Button>
      </div>
    </div>
  );
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <a href="#admin-main-content" className="fixed left-3 top-3 z-[100] -translate-y-20 rounded-md bg-card px-4 py-2 text-sm shadow-md focus:translate-y-0">Ir para o conteúdo</a>
      {!collapsed && <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 border-r bg-card lg:block">{sidebar}</aside>}
      <div className={cn(!collapsed && "lg:pl-60")}>
        <header className="flex h-16 items-center justify-between gap-3 border-b bg-card px-4 sm:px-7">
          <div className="flex min-w-0 items-center gap-3 text-sm">
            <Button ref={menuRef} variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Abrir menu principal" aria-expanded={mobileOpen}><Menu className="size-4" /></Button>
            {collapsed && <Button variant="ghost" size="icon" className="hidden lg:inline-flex" onClick={() => setCollapsed(false)} aria-label="Expandir navegação"><PanelLeft className="size-4" /></Button>}
            <Link to="/" className="hidden text-muted-foreground hover:text-foreground sm:inline">Gunbound</Link>
            <ChevronRight className="hidden size-3 text-muted-foreground sm:block" aria-hidden="true" />
            <span className="truncate">{current?.label ?? "Detalhes"}</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {collapsed && <div className="hidden w-52 sm:block"><NavigationSearch navigation={navigation} shortcut /></div>}
            <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label={dark ? "Usar tema claro" : "Usar tema escuro"}>{dark ? <Sun className="size-4" /> : <Moon className="size-4" />}</Button>
            <div className="mx-1 h-5 border-l" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 px-2" aria-label="Menu da conta">
                  <span className="grid size-7 place-items-center rounded-full border bg-muted text-xs font-medium">{operator?.displayName?.slice(0, 1).toUpperCase() ?? <UserRound className="size-3.5" />}</span>
                  <span className="hidden max-w-40 truncate text-xs sm:inline">{operator?.displayName ?? "Minha conta"}</span>
                  <ChevronDown className="size-3 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="space-y-1 py-3">
                  <p className="truncate">{operator?.displayName ?? "Operador"}</p>
                  <p className="truncate text-xs font-normal text-muted-foreground">{operator?.email}</p>
                  {operator?.roleLabel && <p className="text-xs font-normal text-muted-foreground">{operator.roleLabel}</p>}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={toggleTheme}>{dark ? <Sun /> : <Moon />}{dark ? "Tema claro" : "Tema escuro"}</DropdownMenuItem>
                {onLogout && <DropdownMenuItem disabled={isLoggingOut} onSelect={() => onLogout()}><LogOut />{isLoggingOut ? "Saindo…" : "Sair da conta"}</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main id="admin-main-content" tabIndex={-1} className="mx-auto w-full max-w-[1440px] px-4 py-8 outline-none sm:px-7 lg:px-10 lg:py-10">
          {children ?? <Outlet />}
        </main>
      </div>
      <Dialog open={mobileOpen} onOpenChange={setMobileOpen} title="Navegação" size="sm" contentClassName="fixed inset-y-0 left-0 top-0 h-dvh max-h-dvh w-[min(88vw,18rem)] max-w-none translate-x-0 translate-y-0 rounded-none sm:max-w-none" bodyClassName="flex-1 p-0" onCloseAutoFocus={(event) => { event.preventDefault(); menuRef.current?.focus(); }}>
        {sidebar}
      </Dialog>
    </div>
  );
}
